import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessageSquare,
  Send,
  User,
  ArrowLeft,
  Search,
  Circle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MessagesTab({ getAuthHeaders }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/messages/conversations`, { headers });
      setConversations(res.data);
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch messages for selected conversation
  const fetchMessages = useCallback(async (partnerId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/messages/conversation/${partnerId}`, { headers });
      setMessages(res.data.messages);
      // Update conversation to show as read
      setConversations(prev => prev.map(c => 
        c.partner_id === partnerId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      toast.error("Failed to load messages");
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConversations();
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.partner_id);
    }
  }, [selectedConversation, fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/messages/send`, {
        recipient_id: selectedConversation.partner_id,
        content: newMessage.trim()
      }, { headers });

      setMessages(prev => [...prev, res.data]);
      setNewMessage("");
      
      // Update conversation list
      setConversations(prev => {
        const updated = prev.map(c => 
          c.partner_id === selectedConversation.partner_id 
            ? { ...c, last_message: newMessage.trim(), last_message_time: new Date().toISOString() }
            : c
        );
        // Sort by last message time
        return updated.sort((a, b) => 
          (b.last_message_time || "") > (a.last_message_time || "") ? 1 : -1
        );
      });
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter(c =>
    c.partner_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format timestamp
  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="card-premium p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-olive border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-stone">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="card-premium overflow-hidden">
      <div className="flex h-[600px]">
        {/* Conversations List */}
        <div className={`w-full md:w-1/3 border-r border-sand ${selectedConversation ? 'hidden md:block' : ''}`}>
          <div className="p-4 border-b border-sand">
            <h3 className="font-heading text-lg text-charcoal mb-3">Messages</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="message-search"
              />
            </div>
          </div>
          
          <ScrollArea className="h-[520px]">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-stone">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No conversations yet</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.partner_id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`p-4 border-b border-sand cursor-pointer hover:bg-sand/30 transition-colors ${
                    selectedConversation?.partner_id === conv.partner_id ? 'bg-sand/50' : ''
                  }`}
                  data-testid={`conversation-${conv.partner_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-olive/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-olive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0">
                          <span className="font-medium text-charcoal truncate block">{conv.partner_name}</span>
                          {conv.puppy_names && conv.puppy_names.length > 0 && (
                            <span className="text-xs text-olive truncate block">
                              🐕 {conv.puppy_names.join(", ")}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-stone flex-shrink-0 ml-2">
                          {formatTime(conv.last_message_time)}
                        </span>
                      </div>
                      <p className="text-sm text-stone truncate mt-1">
                        {conv.last_message || "No messages yet"}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-olive text-white flex-shrink-0">
                        {conv.unread_count}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Message Thread */}
        <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : ''}`}>
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-sand flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden"
                  onClick={() => setSelectedConversation(null)}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="w-10 h-10 rounded-full bg-olive/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-olive" />
                </div>
                <div>
                  <h4 className="font-medium text-charcoal">{selectedConversation.partner_name}</h4>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-stone capitalize">{selectedConversation.partner_role}</p>
                    {selectedConversation.puppy_names && selectedConversation.puppy_names.length > 0 && (
                      <p className="text-xs text-olive">• 🐕 {selectedConversation.puppy_names.join(", ")}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-stone">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No messages yet</p>
                      <p className="text-sm mt-1">Send a message to start the conversation</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isOwn = msg.sender_role === "trainer";
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-2xl ${
                              isOwn
                                ? 'bg-olive text-white rounded-br-sm'
                                : 'bg-sand text-charcoal rounded-bl-sm'
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-stone'}`}>
                              {formatTime(msg.created_at)}
                              {isOwn && msg.read && " · Read"}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-sand">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    className="btn-primary"
                    disabled={!newMessage.trim() || sending}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Select a conversation</p>
                <p className="text-sm mt-1">Choose a client from the list to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
